import { pluralizeString } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';

/**
 * `usagePercentile` from GraphQL is a 0–1 score (e.g. 0.82 = 82nd percentile).
 */
export const usagePercentileToLabel = (usagePercentile: number): string => {
  const p = Math.round(usagePercentile * 100);
  if (p <= 10) {
    return 'Rarely used';
  }
  if (p <= 40) {
    return 'Low usage';
  }
  if (p <= 70) {
    return 'Moderate usage';
  }
  if (p <= 90) {
    return 'Frequently used';
  }
  return 'Highly used';
};

const toOrdinalPercentile = (p: number): string => {
  const n = Math.min(100, Math.max(0, Math.round(p * 100)));
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) {
    return `${n}st`;
  }
  if (j === 2 && k !== 12) {
    return `${n}nd`; // # spellchecker:disable-line
  }
  if (j === 3 && k !== 13) {
    return `${n}rd`;
  }
  return `${n}th`;
};

export const formatTableUsagePeriodRange = (
  periodStart: string,
  periodEnd: string,
): string => {
  const start = localizeDate(periodStart);
  const end = localizeDate(periodEnd);
  if (start.isSame(end, 'day')) {
    return start.format('LL');
  }
  if (start.isSame(end, 'month') && start.isSame(end, 'year')) {
    return `${start.format('MMM D')} – ${end.format('D, YYYY')}`;
  }
  if (start.isSame(end, 'year')) {
    return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  }
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
};

export type TableUsageTooltipParts = {
  usagePercentile: number;
  periodStart: string;
  periodEnd: string;
  queriesCount: number;
  uniqueUserCount: number;
};

export const formatTableUsageTooltip = ({
  usagePercentile,
  periodStart,
  periodEnd,
  queriesCount,
  uniqueUserCount,
}: TableUsageTooltipParts): string => {
  const percentileLine = `${toOrdinalPercentile(usagePercentile)} percentile`;
  const rangeLine = formatTableUsagePeriodRange(periodStart, periodEnd);
  const qLabel = pluralizeString(queriesCount, 'query', 'queries');
  const uLabel = pluralizeString(
    uniqueUserCount,
    'unique user',
    'unique users',
  );
  const statsLine = `${qLabel} · ${uLabel}`;
  return [percentileLine, rangeLine, statsLine].join('\n');
};

/**
 * Matches `TableAsset.table_usage` in the API: last-30-day window ending at UTC midnight today.
 * @see packages/py_common/elementary_internal/graphql/schema/types/assets/table_asset.py
 */
export const getTableUsage30dReportingPeriodIso = (): {
  periodStart: string;
  periodEnd: string;
} => {
  const now = new Date();
  const periodEndMs = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    0,
    0,
    0,
    0,
  );
  const periodStartMs = periodEndMs - 30 * 24 * 60 * 60 * 1000;
  return {
    periodStart: new Date(periodStartMs).toISOString(),
    periodEnd: new Date(periodEndMs).toISOString(),
  };
};

export const formatNeverQueriedTableUsageTooltip = (): string => {
  const { periodStart, periodEnd } = getTableUsage30dReportingPeriodIso();
  const rangeLine = formatTableUsagePeriodRange(periodStart, periodEnd);
  const statsLine = `${pluralizeString(
    0,
    'query',
    'queries',
  )} · ${pluralizeString(0, 'unique user', 'unique users')}`;
  return ['Never queried', rangeLine, statsLine].join('\n');
};
